// React Hookis:
import React, { useState } from "react";
// Redux Toolkit Hooks:
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
// Redux Toolkit Actions & State:
import { authentication } from "../../../authentication/authenticationSlice";
import { setFiltersReportsAction } from "../reportsSlice";
// MUI Components:
import {
  Box,
  Button,
  InputAdornment,
  SvgIcon,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
// Components:
import MyCalendar from "../../../global/calendar/MyCalendar";
// MUI Styles:
import { makeStyles } from "@mui/styles";
// MUI SVG Icones:
import SearchIcon from "@mui/icons-material/Search";
// SVG Icones:
import { ReactComponent as calendarIcon } from "../../../../assets/icons/calendar-icon.svg";

const useStyles = makeStyles((theme) => ({
  field: {
    width: "100%",
    borderRadius: "5px",
    backgroundColor: "#DFDFDF",
    "& label.Mui-focused": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#DFDFDF",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: theme.spacing(0),
      paddingRight: theme.spacing(0),
      "& fieldset": {
        borderColor: "#DFDFDF",
      },
      "&:hover fieldset": {
        borderColor: "#DFDFDF",
        borderWidth: 1.5,
      },
      "&.Mui-focused fieldset": {
        borderColor: "#DFDFDF",
        borderWidth: 1.5,
      },
    },
  },
}));

const ReportsSearch = () => {
  const classes = useStyles();

  const theme = useTheme();

  const dispatch = useAppDispatch();

  const { token } = useAppSelector(authentication);

  const [filterData, setFilterData] = useState({
    name: "",
    ip: "",
    fromDate: "",
  });
  const [popoverState, setPopoverState] = useState({
    anchorEl: null,
    popId: -1,
  });

  const handleClick = (event: any, id: number) => {
    setPopoverState({
      anchorEl: event.currentTarget,
      popId: id,
    });
  };
  const handleClose = () => {
    setPopoverState({ anchorEl: null, popId: -1 });
  };

  const open = Boolean(popoverState.anchorEl);

  const id = open ? "popover" : undefined;

  const handleChange = (event: any) => {
    const value = event.target.value;
    setFilterData((last) => ({
      ...last,
      [event.target.name]: value,
    }));
  };

  const textFieldsWidth = 100 / 3;

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!!token) {
          dispatch(
            setFiltersReportsAction({
              name: filterData.name,
              ip: filterData.ip,
              date: filterData.fromDate,
            })
          );
        }
      }}
      sx={{
        width: "100%",
        height: "fit-content",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        margin: "0px 0px 8px 0px",
        [theme.breakpoints.down("md")]: {
          mb: "6px",
        },
      }}
    >
      <Box
        sx={{
          width: "calc(100% - 150px)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          [theme.breakpoints.down("md")]: {
            width: "calc(100% - 100px)",
          },
        }}
      >
        <Box sx={{ width: `calc(${textFieldsWidth}% - 4px)`, mr: "4px" }}>
          <Typography
            sx={{
              width: "100%",
              fontSize: "16px",
              pb: "4px",
              px: "8px",
              [theme.breakpoints.down("md")]: {
                fontSize: "14px",
              },
            }}
          >
            Name
          </Typography>

          <TextField
            name="name"
            type="text"
            value={filterData.name}
            onChange={(event) => {
              handleChange(event);
            }}
            size={"small"}
            sx={{
              label: { color: (theme) => theme.palette.text.secondary },
              input: {
                padding: "8px",
                color: (theme) => theme.palette.text.secondary,
                "&::placeholder": {
                  color: (theme) => theme.palette.text.secondary,
                },
                "&:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px #DFDFDF inset",
                  WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                  transition: " background-color 600000s 0s, color 600000s 0s",
                },
                [theme.breakpoints.down("md")]: {
                  padding: "6px",
                },
              },
              width: "100%",
            }}
            className={classes.field}
          />
        </Box>

        <Box sx={{ width: `calc(${textFieldsWidth}% - 8px)`, mx: "4px" }}>
          <Typography
            sx={{
              width: "100%",
              fontSize: "16px",
              pb: "4px",
              px: "8px",
              [theme.breakpoints.down("md")]: {
                fontSize: "14px",
              },
            }}
          >
            IP Address
          </Typography>

          <TextField
            name="ip"
            type="text"
            value={filterData.ip}
            onChange={(event) => {
              handleChange(event);
            }}
            size={"small"}
            sx={{
              label: { color: (theme) => theme.palette.text.secondary },
              input: {
                padding: "8px",
                color: (theme) => theme.palette.text.secondary,
                "&::placeholder": {
                  color: (theme) => theme.palette.text.secondary,
                },
                "&:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px #DFDFDF inset",
                  WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                  transition: " background-color 600000s 0s, color 600000s 0s",
                },
                [theme.breakpoints.down("md")]: {
                  padding: "6px",
                },
              },
              width: "100%",
            }}
            className={classes.field}
          />
        </Box>

        <Box sx={{ width: `calc(${textFieldsWidth}% - 4px)`, ml: "4px" }}>
          <Typography
            sx={{
              width: "100%",
              fontSize: "16px",
              pb: "4px",
              px: "8px",
              [theme.breakpoints.down("md")]: {
                fontSize: "14px",
              },
            }}
          >
            Date
          </Typography>

          <TextField
            disabled
            name="fromDate"
            type="text"
            value={filterData.fromDate}
            onClick={(event: any) => handleClick(event, 1)}
            aria-describedby={id}
            size={"small"}
            sx={{
              label: { color: (theme) => theme.palette.text.secondary },
              input: {
                padding: "8px",
                cursor: "pointer",
                color: (theme) => theme.palette.text.secondary,
                "&::placeholder": {
                  color: (theme) => theme.palette.text.secondary,
                },
                "&:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px #DFDFDF inset",
                  WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                  transition: " background-color 600000s 0s, color 600000s 0s",
                },
                [theme.breakpoints.down("md")]: {
                  padding: "6px",
                },
                width: "100%",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment sx={{ mx: "12px" }} position="end">
                  {
                    <SvgIcon
                      component={calendarIcon}
                      inheritViewBox
                      sx={{ width: 22, height: 22, cursor: "pointer" }}
                    />
                  }
                </InputAdornment>
              ),
            }}
            className={classes.field}
          />

          <MyCalendar
            vertical="top"
            id={id}
            open={open}
            popoverState={popoverState}
            handleClose={handleClose}
            setFilterData={setFilterData}
            fromDate={filterData.fromDate}
          />
        </Box>
      </Box>
      <Button
        id="ignoreDisableTouch"
        variant="contained"
        color="secondary"
        fullWidth
        type="submit"
        sx={{
          width: "150px",
          height: "39px",
          fontSize: "18px",
          borderRadius: "5px",
          textTransform: "none",
          lineHeight: "0px",
          padding: "12px",
          ml: "8px",
          [theme.breakpoints.down("md")]: {
            width: "100px",
            height: "35px",
            fontSize: "16px",
          },
        }}
      >
        <SvgIcon
          component={SearchIcon}
          inheritViewBox
          sx={{
            width: "auto",
            height: "26px",
            color: "inherit",
            cursor: "pointer",
            mr: "4px",
            [theme.breakpoints.down("md")]: {
              height: "16px",
            },
          }}
        />
        Search
      </Button>
    </Box>
  );
};

export default ReportsSearch;
